<script lang="ts">
  import { Button } from '@/lib/components/ui/button';
  import { Input } from '@/lib/components/ui/input';
  import { type Exercise } from '@/Common/ActivityFunctions';
  import * as Dialog from '$lib/components/ui/dialog';
  import { mergeActivities } from '@/Util/api';
  import { notifyApiError, showErrorToast } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';

  export let activities: Exercise[];
  export let open: boolean = false;

  let icon = 'fa-merge'
  let inProgress = false;

  let name: string | null = null

  let defaultName = "Merged Activity"

  function pickActivityName(activities: Exercise[]) {
    let longestName = null;
    let longestActivity = null;
    for(const a of activities) {
      if (a.data_source == 'Zwift' || a.data_source == 'Strava' || a.renamed === true) {
        if (longestName == null || a.duration > longestActivity?.duration) {
          longestActivity = a
          longestName = a.name
        }
      }
    }
    defaultName = longestName ?? "Merged Activity"
  }
  $: pickActivityName(activities)

</script>

<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open>
  <Dialog.Content class="flex flex-col gap-2 max-h-[95dvh] max-w-[600px] w-[95vw] overflow-y-auto rounded-xl border-0">
    {#if !activities || activities?.length === 0}
      <div class='p-5'>No activities selected for merging.</div>
    {:else if activities.length === 1}
      <div class='p-5'>Please select at least two activities to merge.</div>
    {:else}
      <div class='p-5'>
        <h2 class='text-lg font-semibold mb-4'>Merge Activities</h2>
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Activity Name (Optional)</h3>
        <Input class="mb-4" type="text" bind:value={name} placeholder={defaultName} />
        <div class='mb-4'>
          You are about to merge the following {activities.length} activities:
          <ul class='list-disc list-inside mt-2'>
            {#each activities as activity}
              <li>{activity.name}</li>
            {/each}
          </ul>
        </div>
        <div class='text-sm text-gray-600'>
          Merging activities will combine their data into a single activity. The old activities will be hidden from view, but can be unhidden later if needed.
        </div>
      </div>
    {/if}
    <div class="flex flex-row gap-2 justify-end px-5 py-4 border-t border-border">
      <Button size='sm' variant='outline' on:click={() => open = false}>Cancel</Button>
      <Button size='sm' disabled={activities.length < 2 || inProgress} on:click={async () => {
        inProgress = true
        icon = 'fa-spinner-third fa-spin'
        try {
          let result = await mergeActivities(activities.map(a => a.path), name);
          if (result.success) {
            open = false;
            icon = 'fa-check';
          } else {
            icon = 'fa-xmark';
            notifyApiError(result, { code: ErrorCode.MERGE_FAILED, message: 'Could not merge these activities. Please try again in a moment.' });
          }
        } catch (e) {
          console.error("Error merging activities:", e);
          icon = 'fa-xmark';
          showErrorToast('Could not merge these activities. Please try again in a moment.', ErrorCode.MERGE_THREW);
        }
        inProgress = false
      }}>
        <i class={`fa-solid ${icon} text-xs mr-1`} />
        {#if inProgress}
        Processing
        {:else}
        Confirm Merge
        {/if}
      </Button>
    </div>
  </Dialog.Content>
</Dialog.Root>
